import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";
import { formatSwedishTime } from "../../helpers/DateTime";
import { getStatusChipStyleVars, getStatusLabel } from "../../helpers/StatusMapper";
import { useChargingSse } from "./useChargingSse";
import { mapSampledValuesToSeries } from "./sampledValueSeries";
import { useAuth } from "../../providers/AuthContext";
import { getSampledValuesForConnector, getSampledValuesForSession } from "../../api/ChargingSession";
import LoadingSpinner from "../../components/LoadingSpinner";
import { chartAreaFill, chartGradientDefaults, chartPowerDual } from "../../theme/gitoColors";

const ChargingMetricCurve = ({
    connectorPublicId,
    chargingSessionPublicId,
    isOpen,
    useSse = true,
    showSpinner = true,
    animate = true,
    onLoadingChange,
}) => {
    const [seriesData, setSeriesData] = useState({ soc: [], power: [], powerOffered: [] });
    const [loading, setLoading] = useState(() => Boolean(isOpen));
    const { getAccessToken } = useAuth();
    const { t } = useTranslation();

    const setLoadingState = (isLoading) => {
        setLoading(isLoading);
        if (typeof onLoadingChange === "function") {
            onLoadingChange(isLoading);
        }
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const fetchHistory = async () => {
            setLoadingState(true);
            try {
                const token = await getAccessToken();
                let raw = null;

                if (chargingSessionPublicId) {
                    raw = await getSampledValuesForSession(token, chargingSessionPublicId);
                } else if (connectorPublicId) {
                    raw = await getSampledValuesForConnector(token, connectorPublicId);
                } else {
                    console.warn("⚠️ No connectorPublicId or chargingSessionPublicId provided.");
                    setLoadingState(false);
                    return;
                }

                const list = Array.isArray(raw?.sampledValues)
                    ? raw.sampledValues
                    : Array.isArray(raw?.data)
                        ? raw.data
                        : [];

                setSeriesData(mapSampledValuesToSeries(list));
            } catch (err) {
                console.error("❌ Failed fetching session data:", err);
            } finally {
                setLoadingState(false);
            }
        };

        fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, [isOpen, connectorPublicId, chargingSessionPublicId, getAccessToken]);

    // Live SSE
    useChargingSse(isOpen && useSse && !!connectorPublicId, connectorPublicId, setSeriesData);

    if (!isOpen) return null;

    if (loading && showSpinner) {
        return (
            <div className="flex min-h-96 items-center justify-center">
                <LoadingSpinner text={t("chargeMonitor.loadingDetails")} delayMs={0} />
            </div>
        );
    }

    const allPoints = [...seriesData.soc, ...seriesData.power];
    let min, max;

    if (allPoints.length === 0) {
        const now = Date.now();
        min = now - 60 * 60 * 1000;
        max = now + 60 * 60 * 1000;
    } else if (allPoints.length === 1) {
        const ts = allPoints[0].x;
        const padding = 5 * 60 * 1000;
        min = ts - padding;
        max = ts + padding;
    } else {
        min = Math.min(...allPoints.map(p => p.x));
        max = Math.max(...allPoints.map(p => p.x));
    }

    const chartOptions = {
        chart: {
            type: "area",
            height: 180,
            toolbar: { show: false },
            animations: { enabled: animate, speed: 600, easing: "easeinout" }
        },
        stroke: { curve: "smooth", width: 3, colors: chartAreaFill.colors },
        colors: chartAreaFill.colors,
        fill: {
            type: "gradient",
            gradient: {
                ...chartGradientDefaults,
                gradientToColors: chartAreaFill.gradientToColors,
                opacityTo: 0.12,
            }
        },
        markers: { shape: "circle", size: 4, strokeWidth: 2, strokeColors: "#fff" },
        dataLabels: { enabled: false },
        tooltip: {
            x: { formatter: val => formatSwedishTime(new Date(val)).slice(11, 16) },
            y: { formatter: v => v?.toFixed(1) }
        },
        xaxis: {
            type: "datetime",
            datetimeUTC: false,
            min,
            max,
            tickPlacement: "on",
            labels: { formatter: val => formatSwedishTime(new Date(val)).slice(11, 16) }
        },
        yaxis: {
            min: v => v - 5,
            max: v => v + 5,
            labels: { formatter: v => Number(v).toFixed(0) }
        }
    };

    return (
        <>
            {useSse && (
                <div className="flex items-center mb-2">
                    <span
                        className="text-[9px] px-1 py-[1px] rounded-sm font-bold leading-none badge"
                        style={getStatusChipStyleVars("LIVE")}
                    >
                        {getStatusLabel("LIVE", t)}
                    </span>
                </div>
            )}

            <div className="flex flex-col gap-6 md:grid md:grid-cols-2">
                {/* SoC chart */}
                <div className="rounded-xl border border-mute-200 bg-white p-4 shadow-lg">
                    <h4 className="font-semibold mb-2 text-sm ">{t("chargeMonitor.batteryState")}</h4>
                    <Chart
                        options={{
                            ...chartOptions,
                            yaxis: {
                                min: 0,
                                max: 100,
                                tickAmount: 5,
                                labels: { formatter: v => `${Number(v).toFixed(0)}%` }
                            }
                        }}
                        series={[{ name: t("chargeMonitor.soc"), data: seriesData.soc }]}
                        type="area"
                        height={180}
                    />
                </div>

                <div className="rounded-xl border border-mute-200 bg-white p-4 shadow-lg">
                    <h4 className="font-semibold mb-2 text-sm ">{t("chargeMonitor.chargingSpeed")}</h4>
                    <Chart
                        options={{
                            ...chartOptions,
                            colors: chartPowerDual.colors,
                            fill: {
                                type: "gradient",
                                gradient: {
                                    ...chartGradientDefaults,
                                    gradientToColors: chartPowerDual.gradientToColors,
                                    opacityTo: 0.12,
                                },
                            },
                            stroke: { curve: "smooth", width: 3 },
                            yaxis: {
                                min: () => {
                                    const values = [...seriesData.power, ...seriesData.powerOffered].map(p => p.y);
                                    if (values.length === 0) return 0;
                                    return Math.max(0, Math.min(...values) - 50);
                                },
                                max: () => {
                                    const values = [...seriesData.power, ...seriesData.powerOffered].map(p => p.y);
                                    if (values.length === 0) return 1000;
                                    const rawMax = Math.max(...values) + 50;
                                    return Math.ceil(rawMax / 50) * 50;
                                },
                                tickAmount: 5,
                                labels: { formatter: v => Number(v).toFixed(0) }
                            }
                        }}
                        series={[
                            { name: t("chargeMonitor.offered"), data: seriesData.powerOffered },
                            { name: t("status.CHARGING"), data: seriesData.power }
                        ]}
                        type="area"
                        height={180}
                    />

                </div>
            </div>
        </>
    );
};

export default React.memo(ChargingMetricCurve);